import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline'
import ListaNarzedzi from './ListaNarzedzi'

const KLUCZ = 'vv-pasek-narzedzi-zwiniety'

/**
 * Szerokość okna, od której pasek startuje ROZWINIĘTY (dopóki ktoś sam go nie przełączy).
 *
 * Rozwinięty (256 px) mieści się w lewym marginesie bez przesuwania treści 80rem dopiero od
 * 80rem + 2 × (256 + 8) px = 1808 px (patrz `.vv-obok-paska` w index.css). Właściciel nie chce, żeby
 * treść uciekała w prawo (2026-10-07), więc węższe okna startują ze zwiniętą szyną (64 px), która
 * mieści się w marginesie już od ok. 1424 px.
 */
export const PROG_ROZWINIETEGO = 1808

// Muszą się zgadzać z klasami `w-16` / `w-64` niżej. Zmienną czytają odsunięcie treści
// (`.vv-obok-paska`) i kafelek zgłaszania błędów (UkladZPaskiem → ObokPaska).
const SZEROKOSC = { zwiniety: '4rem', rozwiniety: '16rem' }

// Zapamiętany wybór: true / false, albo null, gdy nikt jeszcze nie klikał.
// Prywatny tryb przeglądarki potrafi rzucić wyjątkiem przy samym odczycie.
const zapamietany = () => {
  try {
    const wartosc = localStorage.getItem(KLUCZ)
    if (wartosc === '1') return true
    if (wartosc === '0') return false
  } catch (e) { /* bez pamięci pasek po prostu startuje od reguły szerokości */ }
  return null
}

const waskieOkno = () => window.innerWidth < PROG_ROZWINIETEGO

/**
 * Lewy pasek narzędzi na komputerze (od `lg`). Poniżej `lg` ta sama lista otwiera się z przycisku
 * menu w górnym pasku (SzufladaNarzedzi).
 *
 * Leży w lewym marginesie strony (`absolute` na wysokość treści, lista `sticky` przy przewijaniu),
 * więc kończy się nad stopką. Na stronie głównej startuje zwinięty niezależnie od zapamiętanego
 * wyboru, bo tam narzędzia stoją też w górnym pasku (decyzja właściciela 2026-10-07); przełączenie
 * na stronie głównej działa do jej opuszczenia i nie jest zapamiętywane.
 */
const PasekNarzedzi = () => {
  const { t } = useTranslation('wspolne')
  const { pathname } = useLocation()
  const naGlownej = pathname === '/'
  const [wybor, setWybor] = useState(zapamietany)
  const [waskie, setWaskie] = useState(waskieOkno)
  const [naGlownejRozwiniety, setNaGlownejRozwiniety] = useState(false)
  const zwiniety = naGlownej ? !naGlownejRozwiniety : (wybor === null ? waskie : wybor)

  useEffect(() => { if (!naGlownej) setNaGlownejRozwiniety(false) }, [naGlownej])

  // Dopóki nikt nie wybrał sam, pasek idzie za szerokością okna. Po pierwszym
  // kliknięciu decyduje już tylko zapamiętany wybór.
  useEffect(() => {
    if (wybor !== null || typeof window.matchMedia !== 'function') return undefined
    const zapytanie = window.matchMedia(`(min-width: ${PROG_ROZWINIETEGO}px)`)
    const zmiana = () => setWaskie(!zapytanie.matches)
    zapytanie.addEventListener('change', zmiana)
    return () => zapytanie.removeEventListener('change', zmiana)
  }, [wybor])

  useEffect(() => {
    const korzen = document.documentElement
    korzen.style.setProperty('--pasek-narzedzi', zwiniety ? SZEROKOSC.zwiniety : SZEROKOSC.rozwiniety)
    return () => korzen.style.removeProperty('--pasek-narzedzi')
  }, [zwiniety])

  const przelacz = () => {
    if (naGlownej) { setNaGlownejRozwiniety((v) => !v); return }
    const nowy = !zwiniety
    setWybor(nowy)
    try { localStorage.setItem(KLUCZ, nowy ? '1' : '0') } catch (e) { /* jak wyżej */ }
  }

  const opisPrzycisku = zwiniety ? t('narzedzia.rozwin_pasek') : t('narzedzia.zwin_pasek')

  return (
    <div
      className={`hidden lg:block absolute inset-y-0 left-0 z-10 print:hidden bg-white border-r border-blue-100
                  transition-[width] duration-200 motion-reduce:transition-none
                  ${zwiniety ? 'w-16' : 'w-64'}`}
    >
      <nav aria-label={t('narzedzia.przycisk')} className='sticky top-16 flex flex-col h-[calc(100vh-4rem)]'>
        {/* Wysokości policzone tak, żeby cała lista mieściła się bez przewijania
            na ekranie 1366x768. Niższe okno przewija samą listę, a przycisk
            zwijania zostaje na dole. */}
        <div id='pasek-narzedzi-lista' className='flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 [scrollbar-width:thin]'>
          <ListaNarzedzi zwiniety={zwiniety} />
        </div>
        <div className='px-3 py-2 border-t border-blue-100'>
          <button
            type='button'
            onClick={przelacz}
            aria-expanded={!zwiniety}
            aria-controls='pasek-narzedzi-lista'
            title={zwiniety ? opisPrzycisku : undefined}
            className={`flex items-center rounded-xl text-sm font-semibold text-blue-600
                        hover:text-blue-700 hover:bg-blue-50 transition-colors
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                        ${zwiniety ? 'justify-center h-9 w-10 mx-auto' : 'w-full gap-3 px-3 py-1.5'}`}
          >
            {zwiniety
              ? <ChevronDoubleRightIcon aria-hidden='true' className='w-5 h-5 flex-shrink-0' />
              : <ChevronDoubleLeftIcon aria-hidden='true' className='w-5 h-5 flex-shrink-0' />}
            <span className={zwiniety ? 'sr-only' : 'whitespace-nowrap'}>{opisPrzycisku}</span>
          </button>
        </div>
      </nav>
    </div>
  )
}

export default PasekNarzedzi
